import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getInspectionsByInspector, InspectionByInspector } from '@/services/dashboardService';
import { getCurrentMonth } from '@/utils/date';

import { DataStatus } from './dataStatus';
import { LoadingSpinner } from './LoadingSpinner';
import MonthSelector from './MonthSelector';
import './dashboard.css';
import chartColors from './chartColors';

const USE_MOCK_INSPECTOR_DEMO = false; // Set to true to use mock data for inspectors, false to fetch real data from the API

const generateMockInspectors = (month: number, year: number, total = 1000): InspectionByInspector[] => {
    const countries = ['Indonesia', 'Singapore', 'Malaysia', 'Australia', 'Japan'];

    return Array.from({ length: total }, (_, index) => ({
        yearInspection: year,
        monthInspection: month,
        totalInspection: Math.floor(Math.random() * 20),
        inspectorId: `mock-inspector-${index + 1}`,
        inspectorName: `Inspector ${String(index + 1).padStart(4, '0')}`,
        acceptedNominationInspectionProcess: 0,
        cancelledNominationInspectionProcess: 0,
        inProgressNominationInspectionProcess: 0,
        inProgressInspectionProcess: 0,
        finishedInspectionProcess: 0,
        finishedInspectionProcessNotYetInvoices: 0,
        invoicesSentToOperator: 0,
        invoicesInFinance: 0,
        invoicesSentToOperatorandApprove: 0,
        invoicesInProgress: 0,
        inspectionInvoicesCompleted: 0,
        inspectionInvoicesPaid: 0,
        inspectionInvoicesNotYetPaid: 0,
        inspectorCountry: countries[index % countries.length],
    }));
};

const transformData = (originalData: InspectionByInspector[]) => {
    const inspectors: string[] = [];
    const inspections: number[] = [];

    const sortedData = [...originalData].sort((a, b) => {
        if (b.totalInspection !== a.totalInspection) {
            return b.totalInspection - a.totalInspection;
        }

        return a.inspectorName.localeCompare(b.inspectorName);
    });

    sortedData.forEach((inspector) => {
        inspectors.push(inspector.inspectorName + (inspector.inspectorCountry ? ` (${inspector.inspectorCountry})` : ''));
        inspections.push(inspector.totalInspection);
    });

    return { inspectors, inspections };
};

const ChartInspectionsByInspector = () => {
    const {year: currentYear, month: currentMonth} = getCurrentMonth();

    const [inspectorNames, setInspectorNames] = useState<string[]>([]);
    const [inspections, setInspections] = useState<number[]>([]);
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.LOADING);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    useEffect(() => {
        const getData = async () => {
            setDataStatus(DataStatus.LOADING);

            if (USE_MOCK_INSPECTOR_DEMO) {
                const mockData = generateMockInspectors(month, year, 100);
                const transformedData = transformData(mockData);
                setInspectorNames(transformedData.inspectors);
                setInspections(transformedData.inspections);
                setDataStatus(DataStatus.OK);
                return;
            }

            const response = await getInspectionsByInspector(month, year);

            if (!response.data) {
                setDataStatus(DataStatus.ERROR);
                return;
            }

            if (response.data.length === 0) {
                setDataStatus(DataStatus.EMPTY);
                return;
            }

            const originalData = response.data as InspectionByInspector[];
            const transformedData = transformData(originalData);
            setInspectorNames(transformedData.inspectors);
            setInspections(transformedData.inspections);
            setDataStatus(DataStatus.OK);
        };

        getData();
    }, [month, year]);

    const series = [{
        name: 'Total Inspections',
        data: inspections,
    }];

    const chartHeight = Math.max(600, inspectorNames.length * 48);
    const longestInspectorLabel = Math.max(...inspectorNames.map((name) => name.length), 0);
    const chartWidth = Math.max(700, (longestInspectorLabel * 8) + 360);
    const showDataLabels = inspectorNames.length <= 15;

    const chartOptions: ApexCharts.ApexOptions = {
        chart: {
            type: 'bar',
            toolbar: {
                show: true,
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '70%',
                dataLabels: {
                    position: 'top'
                },
            },
        },
        dataLabels: {
            enabled: showDataLabels,
            offsetX: -8,
            style: {
                fontSize: '11px',
            },
        },
        series,
        xaxis: {
            categories: inspectorNames,
            title: {
                text: 'Total Inspections',
            },
        },
        yaxis: {
            labels: {
                maxWidth: 280,
                style: {
                    fontSize: '11px',
                },
            },
        },
        tooltip: {
            y: {
                formatter: (value) => `${value} inspections`,
            },
        },
        colors: [chartColors.red],
    };

    return (
        <div className='card card-bar'>
            <h2 className='card-title'>Inspections by Inspector</h2>
            <div>
                <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear) }} />
                {(dataStatus === DataStatus.LOADING) && <LoadingSpinner />}
            </div>
            {(dataStatus === DataStatus.EMPTY || dataStatus === DataStatus.ERROR) && <div>No data to display</div>}
            {(dataStatus === DataStatus.OK || dataStatus === DataStatus.LOADING) &&
                <div className='chart-scroll-container'>
                    <ReactApexChart
                        type="bar"
                        options={chartOptions}
                        series={series}
                        height={chartHeight}
                        width={chartWidth}
                    />
                </div>
            }
        </div>
    );
};

export default ChartInspectionsByInspector;
